// Mounts the Studio overlay when isStudio becomes true. Tears it down on exit.

import { effect } from '@/core/signals';
import { isStudio, findDotgradLayer } from '@/lib/studioMode';
import { state, setTime } from '@/core/state';
import { el } from '@/lib/dom';
import { mountStudioPanel } from './panel';
import { attachCanvasDrag } from './canvasDrag';

let panelHost: HTMLElement | null = null;
let disposePanel: (() => void) | null = null;
let disposeDrag: (() => void) | null = null;
let disposeMotion: (() => void) | null = null;
let hint: HTMLElement | null = null;
let hintTimer: ReturnType<typeof setTimeout> | null = null;

// Motion loop — drives state.time at ~60Hz so motion previews are smooth
// regardless of the user's fps setting (which is meant for GIF/WebM export).
// Uses setInterval(16ms) which works in visible tabs at 60Hz and falls back
// gracefully under background-tab throttling.
let motionTimer: ReturnType<typeof setInterval> | null = null;

function startMotionLoop(): void {
  if (motionTimer !== null) return;
  motionTimer = setInterval(() => {
    if (!isStudio.peek()) { stopMotionLoop(); return; }
    if (!state.peek().paused) {
      setTime(state.peek().time + 1);
    }
  }, 16);
}

function stopMotionLoop(): void {
  if (motionTimer !== null) {
    clearInterval(motionTimer);
    motionTimer = null;
  }
}

function watchMotion(): () => void {
  return effect(() => {
    if (!isStudio.get()) {
      stopMotionLoop();
      return;
    }
    // Re-subscribe to layer params via state.get()
    void state.get();
    const layer = findDotgradLayer();
    const motion = (layer?.params['motion'] as string | undefined) ?? 'none';
    if (motion !== 'none') startMotionLoop();
    else stopMotionLoop();
  });
}

export function watchStudio(): () => void {
  const studioDispose = effect(() => {
    if (isStudio.get()) {
      if (!panelHost) {
        panelHost = el('div', { class: 'st-panel', role: 'region', ariaLabel: 'Studio dock' });
        document.body.appendChild(panelHost);
        disposePanel = mountStudioPanel(panelHost);
        // Attach canvas drag after one frame so the canvas exists in this layout.
        requestAnimationFrame(() => {
          disposeDrag = attachCanvasDrag();
        });
        showHint('Studio  ·  drag canvas to angle/spread  ·  scroll for pixel  ·  M to exit');
      }
    } else {
      if (panelHost) {
        disposePanel?.();
        disposePanel = null;
        disposeDrag?.();
        disposeDrag = null;
        panelHost.remove();
        panelHost = null;
      }
      stopMotionLoop();
    }
  });
  disposeMotion = watchMotion();
  return () => {
    studioDispose();
    disposeMotion?.();
    disposeMotion = null;
    stopMotionLoop();
  };
}

function showHint(text: string): void {
  if (typeof document === 'undefined') return;
  if (hintTimer !== null) clearTimeout(hintTimer);
  if (!hint) {
    hint = el('div', { class: 'st-hint mono' });
    document.body.appendChild(hint);
  }
  hint.textContent = text;
  hint.classList.add('show');
  hintTimer = setTimeout(() => hint?.classList.remove('show'), 1800);
}
